/* assets/compare.js — Lectric Compare Drawer
   model-aware, robust fallbacks, cross-page sync + polling + compare-page URL sync (user-initiated guards)
*/
(function () {
  'use strict';

  /* ----------------- Guard: hide drawer on blocked pages, but keep logic alive ----------------- */
  const blocked = (window.lectricCanCompare === false) || document.body.classList.contains('no-compare');
  if (blocked) {
    const stray = document.getElementById('compare-drawer');
    if (stray) stray.remove();
  }
  // Do NOT return; checkboxes & storage should still function on blocked pages.

  /* ----------------- Config ----------------- */
  const MAX = 4;
  const LEGACY_STORAGE_KEY = 'lectric_compare_v1';
  const STORAGE_KEY = 'lectric_compare_v2';          // <- new primary
  const STORAGE_SEQ_KEY = 'lectric_compare_seq';
  const PAGE_LAST_SEEN_SEQ = 'lectric_compare_last_seen_seq';
  // MOD: Add a key for storing the drawer's UI state in sessionStorage.
  const DRAWER_STATE_KEY = 'lectric_compare_drawer_state';
  const STORAGE_TTL_HOURS = 24;
  const STORAGE_MODEL_CACHE_KEY = 'lectric_compare_cache_v1';


  // ---- Write guard for this page (prevents legacy scripts from clobbering the drawer)
  const __origLocalSet = localStorage.setItem.bind(localStorage);
  const __origProtoSet = Storage.prototype.setItem;
  const __compareWriteGate = { ok: false };

  localStorage.setItem = function (k, v) {
    try {
      if (k === STORAGE_KEY && !__compareWriteGate.ok) return; // swallow rogue write
    } catch (_) {}
    return __origLocalSet(k, v);
  };

  Storage.prototype.setItem = function (k, v) {
    try {
      if (k === STORAGE_KEY && !__compareWriteGate.ok) return; // swallow rogue write (prototype path)
    } catch (_) {}
    return __origProtoSet.call(this, k, v);
  };

  /* Broadcast channel for same-origin tabs/pages */
  let bc = null;
  try { bc = new BroadcastChannel('lectric-compare-sync'); } catch (_) {}

  /* ----------------- Helpers ----------------- */
  const norm = (s) => (s || '').toString().toLowerCase().trim();

  // --- Data-driven alias map (single source of truth) ---
  const MODEL_ALIASES = {
    xp4:        ['xp-4', 'xp 4', 'xp4', 'bike-4'],
    trike2:     ['trike', 'trike-2', 'xp trike', 'xp-trike', 'xp-trike-2'],
    xpedition2: ['xpedition', 'xped-2', 'xp edition', 'xp-edition', 'xpedition-2'],
    xpeak2:     ['xpeak', 'xp-peak', 'xpeak-2'],
    xplite2:    ['xp lite', 'xp-lite', 'xpl-2', 'lite2', 'lite 2', 'lite-2'],
    xpress:     ['xpress', 'xpress-1', 'xpress1'],
    one:        ['one', 'lectric one']
    // add new families here ↓
    // alpha3: ['alpha3', 'alpha-3', 'alpha 3', 'xp alpha 3']
  };

  function canonicalModelKey(raw) {
    const s = (raw || '').toLowerCase().trim();
    if (!s) return '';

    // 1) direct alias hits
    for (const [key, aliases] of Object.entries(MODEL_ALIASES)) {
      if (aliases.some(a => s.includes(a))) return key;
    }

    // 2) structured fallbacks (broad, last)
    if (/\bxp[\s-]*4(\.0)?\b|\bbike-?4\b/.test(s)) return 'xp4';
    if (/\btrike\b/.test(s)) return 'trike2';
    if (/\bxp[\s-]*edition|\bxpedition|\bxped-?2\b/.test(s)) return 'xpedition2';
    if (/\bxp[\s-]*peak|\bxpeak\b/.test(s)) return 'xpeak2';
    if (/\bxp[\s-]*lite|\bxpl[\s-]*2|\blite-?2\b|\blite\s*2(\.0)?\b/.test(s)) return 'xplite2';
    if (/\bxpress\b/.test(s)) return 'xpress';
    if (/\bone\b/.test(s)) return 'one';

    // 3) last-ditch generic xp → xp4
    if (/\bxp\b/.test(s)) return 'xp4';

    return '';
  }

  // (Optional) helper so you can extend aliases at runtime without editing the file:
  window.LectricCompare = window.LectricCompare || {};
  window.LectricCompare.extendModelAliases = function (key, ...aliases) {
    const k = (key || '').toLowerCase().trim();
    if (!k) return;
    MODEL_ALIASES[k] = Array.from(new Set([...(MODEL_ALIASES[k] || []), ...aliases.map(a => a.toLowerCase())]));
  };

  function deriveModelKey(obj) {
    // modelKey hint
    if (obj && obj.modelKey) {
      const mk = canonicalModelKey(obj.modelKey);
      if (mk) return mk;
    }
    // handle/title (move this UP so it beats brittle "type" strings)
    if (obj && (obj.handle || obj.title)) {
      const mk = canonicalModelKey(obj.handle || obj.title);
      if (mk) return mk;
    }
    // NEW: try to extract from variant URL (/products/<handle>)
    if (obj && obj.vurl) {
      try {
        const u = new URL(obj.vurl, location.origin);
        const parts = u.pathname.split('/').filter(Boolean);
        const idx = parts.indexOf('products');
        const handle = (idx > -1 && parts[idx + 1]) ? parts[idx + 1] : (parts[parts.length - 1] || '');
        const mk = canonicalModelKey(handle);
        if (mk) return mk;
      } catch (_) {
        const path = (obj.vurl || '').split('?')[0].split('#')[0];
        const handle = path.split('/').filter(Boolean).pop() || '';
        const mk = canonicalModelKey(handle);
        if (mk) return mk;
      }
    }
    // product type (move this DOWN — last resort)
    if (obj && obj.type) {
      const mk = canonicalModelKey(obj.type);
      if (mk) return mk;
    }
    return '';
  }


  function displayModelName(mk) {
    if (!mk) return '';
    return mk
      .replace(/^xplite(\d+)/, (_, n) => `XP Lite ${n}`)
      .replace(/^(xp)(\d+)/, (_, a, b) => `${a.toUpperCase()}${b}`)
      .replace(/^trike(\d+)/, 'Trike $1')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  const now = () => Date.now();

  function load() {
    const fallback = { items: [], ts: now() };
    try {
      // Prefer v2
      const rawV2 = localStorage.getItem(STORAGE_KEY);
      if (rawV2) {
        const data = JSON.parse(rawV2);
        // TTL
        if ((now() - (data.ts || 0)) > STORAGE_TTL_HOURS * 3600 * 1000) return fallback;
        data.items = (data.items || []).map(it => {
          const mk = deriveModelKey(it);
          return mk ? { ...it, modelKey: mk } : it;
        });
        return data;
      }

      // One-time migration from v1 → v2 (do NOT keep reading v1 afterward)
      const rawV1 = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (rawV1) {
        const data = JSON.parse(rawV1);
        data.items = (data.items || []).map(it => {
          const mk = deriveModelKey(it);
          return mk ? { ...it, modelKey: mk } : it;
        });
        // write to v2 only
        __compareWriteGate.ok = true;
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: data.items, ts: now() }));
        } finally {
          __compareWriteGate.ok = false;
        }
        return { items: data.items, ts: now() };
      }

      return fallback;
    } catch {
      return fallback;
    }
  }

  function bumpSeq() {
    const current = parseInt(localStorage.getItem(STORAGE_SEQ_KEY) || '0', 10) || 0;
    const next = current + 1;
    localStorage.setItem(STORAGE_SEQ_KEY, String(next));
    // remember on this page
    try { sessionStorage.setItem(PAGE_LAST_SEEN_SEQ, String(next)); } catch (_) {}
    // notify listeners
    try { bc && bc.postMessage({ type: 'compare:seq', seq: next }); } catch (_) {}
  }

  function save(items) {
    const prev = load().items || [];
    // Build a safe list: skip anything we cannot resolve to a real model key
    const merged = (items || [])
      .map(it => {
        // Try: explicit modelKey → type → handle/title → vurl
        let mk =
          canonicalModelKey(it.modelKey) ||
          canonicalModelKey(it.type)     ||
          canonicalModelKey(it.handle || it.title) ||
          (it.vurl ? canonicalModelKey(__extractHandleFromUrl(it.vurl)) : '');
        if (!mk) return null; // drop unresolvable entries
        const old = prev.find(p => canonicalModelKey(p.modelKey) === mk) || {};
        return {
          ...old,
          ...it,
          modelKey: mk,
          handle:   norm(it.handle || old.handle || ''),
          title:    it.title || old.title || displayModelName(mk),
          image:    it.image || old.image || '',
          color:    it.color || old.color || '',
          vurl:     it.vurl  || old.vurl  || ''
        };
      })
      .filter(Boolean);

      const data = { items: merged, ts: now() };

    __compareWriteGate.ok = true;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } finally {
      __compareWriteGate.ok = false;
    }

    bumpSeq();
    try { window.dispatchEvent(new CustomEvent('compare:changed', { detail: merged })); } catch (_) {}
  }


  function add(item) {
    const mk = deriveModelKey(item);
    if (!mk) return;

    // --- New Cache Logic ---
    // When a rich item is added, save its details to our permanent cache.
    if (item.title && item.image) {
      try {
        const cacheRaw = localStorage.getItem(STORAGE_MODEL_CACHE_KEY);
        const cache = cacheRaw ? JSON.parse(cacheRaw) : {};
        // Only update if we don't have a title or the new title is better (not a handle).
        if (!cache[mk]?.title || item.title.includes(' ')) {
          cache[mk] = { title: item.title, image: item.image, handle: item.handle, vurl: item.vurl };
          localStorage.setItem(STORAGE_MODEL_CACHE_KEY, JSON.stringify(cache));
        }
      } catch (_) {}
    }
    // --- End New Cache Logic ---

    const data = load();
    const idx = data.items.findIndex(x => canonicalModelKey(x.modelKey) === mk);
    const incoming = { ...item, modelKey: mk, handle: norm(item.handle || ''), title: item.title || '', image: item.image || '', color: item.color || '', vurl:  item.vurl  || '' };

    if (idx > -1) {
      const prev = data.items[idx] || {};
      const merged = { ...prev, ...incoming, title: incoming.title || prev.title || displayModelName(mk), image: incoming.image || prev.image || '', color: incoming.color || prev.color || '', vurl:  incoming.vurl  || prev.vurl  || '' };
      const next = data.items.slice();
      next[idx] = merged;
      save(next);
      return next;
    }

    if (data.items.length >= MAX) return data.items;
    const first = { ...incoming, title: incoming.title || displayModelName(mk) };
    const next = data.items.concat([first]);
    save(next);
    return next;
  }

  function removeModel(modelKey) {
    const mk = canonicalModelKey(modelKey);
    const data = load();
    const next = data.items.filter(x => canonicalModelKey(x.modelKey) !== mk);
    save(next);
    return next;
  }

  function removeModelStrict(modelKey) {
    const key = (modelKey || '').toString().toLowerCase().trim();
    const data = load();
    const next = data.items.filter(x => (x.modelKey || '').toLowerCase().trim() !== key);
    save(next);
    return next;
  }

  function clearAll() { save([]); }

    // Build-and-save from an array of model keys (URL → storage), preserving known details
    function forceFromModels(tokens, opts = {}) {
      // Don’t clobber a non-empty session unless explicitly told to.
      const preferSession = (opts.preferSession !== false); // default true
      const existing = (load().items || []);
      if (preferSession && existing.length) return;

      const prev = load().items || [];
      const byKey = new Map(prev.map(p => [canonicalModelKey(p.modelKey || p.handle || p.title), p]));

      // Accept either canonical model keys OR product handles
      const cleaned = (tokens || [])
        .map(t => (t || '').toString().toLowerCase().trim())
        .filter(Boolean);

      const next = cleaned.map(t => {
        const mk = canonicalModelKey(t);   // works for either a key or a handle
        if (!mk) return null;

        const old = byKey.get(mk) || {};

        // If it looks like a product handle, carry it through
        const looksLikeHandle = t.includes('-') || (!MODEL_ALIASES[mk] || !MODEL_ALIASES[mk].includes(t));

        return {
          modelKey: mk,
          handle:   old.handle || (looksLikeHandle ? t : ''),
          title:    old.title  || displayModelName(mk),
          image:    old.image  || '',
          color:    old.color  || '',
          vurl:     old.vurl   || '' // unknown exact variant for now
        };
      }).filter(Boolean);

      save(next);
    }



  /* ----------------- Public API ----------------- */
  window.LectricCompare = {
    add,
    removeModel,
    removeModelStrict,
    clearAll,
    load,
    save,
    canonicalModelKey,
    STORAGE_KEY,
    // ✅ EXPOSE forceFromModels so the compare page can sync storage from URL/models
    forceFromModels
  };

  /* ----------------- Drawer DOM (optional; script works without it) ----------------- */
  const drawer  = document.getElementById('compare-drawer');
  const hasDrawer = !!drawer;

  const toggle  = hasDrawer ? drawer.querySelector('[data-compare-toggle]') : null;
  const panel   = hasDrawer ? drawer.querySelector('[data-compare-panel]')  : null;
  const itemsEl = hasDrawer ? drawer.querySelector('[data-compare-items]')  : null;
  const goLink  = hasDrawer ? drawer.querySelector('[data-compare-go]')     : null;

  // MOD: This new function reads the saved state and applies it to the drawer.
  function applyInitialDrawerState() {
    if (!hasDrawer) return;
    try {
      const savedState = sessionStorage.getItem(DRAWER_STATE_KEY);
      // Only apply a saved state if the drawer has items.
      // Defaults to expanded if no state is saved.
      if ((load().items || []).length > 0 && savedState === 'minimized') {
        drawer.classList.add('compare-drawer--minimized');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      } else {
        drawer.classList.remove('compare-drawer--minimized');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      }
    } catch (_) {}
  }

  function getBaseCompareUrl() {
    if (!hasDrawer) return '/pages/compare-lectric-ebikes';
    return (
      drawer.getAttribute('data-compare-url') ||
      (goLink && goLink.getAttribute('href')) ||
      '/pages/compare-lectric-ebikes'
    );
  }

  function forceExpandDrawer() {
    if (!hasDrawer) return;

    // Only do work if the drawer is actually minimized.
    if (!drawer.classList.contains('compare-drawer--minimized')) return;

    // Remove the class to expand the drawer.
    drawer.classList.remove('compare-drawer--minimized');
    
    // Update the toggle button's accessibility state.
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    
    // IMPORTANT: Update the session state so it stays expanded on the next page.
    try {
      sessionStorage.setItem(DRAWER_STATE_KEY, 'expanded');
    } catch (_) {}
    
    // Recalculate the panel's height for a smooth animation.
    setPanelHeight();
  }

  function setPanelHeight() {
    if (!hasDrawer || !panel) return;
    drawer.style.setProperty('--compare-panel-h', panel.scrollHeight + 'px');
  }

  function ensureOpen() {
    if (!hasDrawer) return;
    // Don't force it open if the user intentionally minimized it.
    const isMinimized = drawer.classList.contains('compare-drawer--minimized');
    if (isMinimized) return;
    
    drawer.classList.remove('compare-drawer--minimized');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  // ---- DROP-IN REPLACEMENT ----------------------------------------------------
  function syncCheckboxes() {
    const data = load();

    // What’s selected? Keep model -> { productHandle, variantHandle, variantId, perfKey }
    const chosenByModel = new Map();
    (data.items || []).forEach(x => {
      const mk = deriveModelKey(x);
      if (!mk) return;
      const vurl = x.vurl || '';
      chosenByModel.set(mk, {
        productHandle: (x.handle || '').toLowerCase().trim(),
        variantHandle: __extractHandleFromUrl(vurl).toLowerCase().trim(),
        variantId:     (x.variantId || __extractVariantId(vurl) || '').trim(),
        perfKey:       (x.perf || '').toLowerCase().trim()
      });
    });

    const count      = (data.items || []).length;
    const atCapacity = count >= MAX;

    document.querySelectorAll('[data-compare]').forEach(el => {
      const input     = el.querySelector('input[type="checkbox"]');
      const labelSpan = el.querySelector('.compare-checkbox__label-text');
      if (!input || !labelSpan) return;

      const row = el.closest('.compare-option, [data-compare-row]');
      if (row) row.classList.remove('is-selected-model', 'is-active-variant');

      const obj = {
        modelKey: el.getAttribute('data-model-key') ||
                  el.getAttribute('data-product-type') ||
                  el.getAttribute('data-product-handle') || '',
        type:   el.getAttribute('data-product-type') || '',
        handle: el.getAttribute('data-product-handle') || '',
        title:  el.getAttribute('data-product-title')  || ''
      };
      const mk = deriveModelKey(obj);

      if (!mk) {
        input.checked = false;
        input.disabled = false;
        el.classList.remove('compare-checkbox--disabled', 'compare-checkbox--ghosted');
        el.removeAttribute('aria-disabled');
        labelSpan.textContent = 'Add to compare';
        return;
      }

      const chosen = chosenByModel.get(mk);
      const isUnified = __isUnifiedCardEl(el);

      const thisVariantUrl    = el.getAttribute('data-variant-url') || '';
      const thisVariantHandle = __extractHandleFromUrl(thisVariantUrl).toLowerCase().trim();
      const thisVariantId     = (__extractVariantId(thisVariantUrl) || '').trim();
      const thisProductHandle = (el.getAttribute('data-product-handle') || '').toLowerCase().trim();
      const thisPerfKey       = (
        el.getAttribute('data-perf-key') ||
        el.closest('[data-perf-key]')?.getAttribute('data-perf-key') ||
        ''
      ).toLowerCase().trim();

      el.classList.remove('compare-checkbox--disabled', 'compare-checkbox--ghosted');
      el.removeAttribute('aria-disabled');

      if (!chosen) {
        input.checked = false;
        input.disabled = atCapacity;
        if (atCapacity) {
          el.classList.add('compare-checkbox--disabled');
          el.setAttribute('aria-disabled', 'true');
        }
        labelSpan.textContent = 'Add to compare';
        return;
      }

      if (row) row.classList.add('is-selected-model');

      // Decide if THIS control is the active one that should show "Remove from compare"
      let isActive = false;

      if (isUnified) {
        // 1) Prefer perf matching for unified split cards
        if (chosen.perfKey && thisPerfKey) {
          isActive = (chosen.perfKey === thisPerfKey);
        } else if (chosen.variantId && thisVariantId) {
          // Fallback: exact variant id match
          isActive = (chosen.variantId === thisVariantId);
        } else {
          // Last resort: product/handle match
          isActive =
            (chosen.variantHandle && thisVariantHandle && (chosen.variantHandle === thisVariantHandle)) ||
            (!thisVariantHandle && chosen.productHandle && (thisProductHandle === chosen.productHandle));
        }
      } else {
        // Standard tiles: require variantId match when available
        if (chosen.variantId && thisVariantId) {
          isActive = (chosen.variantId === thisVariantId);
        } else {
          // If no variant id on the control (rare), fall back to handle comparison
          isActive =
            (chosen.variantHandle && thisVariantHandle && (chosen.variantHandle === thisVariantHandle)) ||
            (!thisVariantHandle && chosen.productHandle && (thisProductHandle === chosen.productHandle));
        }
      }

      if (isActive) {
        input.checked = true;
        input.disabled = false;
        labelSpan.textContent = 'Remove from compare';
        if (row) row.classList.add('is-active-variant');
        return;
      }

      // Same model, different perf/variant → show "Added to compare" (checked + disabled)
      input.checked = true;
      input.disabled = true;
      el.classList.add('compare-checkbox--ghosted');
      el.setAttribute('aria-disabled', 'true');
      labelSpan.textContent = 'Added to compare';
    });
  }


  /* ----------------- Drawer render ----------------- */
  function render() {
    const data = load();
    const count = data.items.length;

    // Always keep card checkboxes in sync, even without a drawer
    // applyStoredVariantUrlsToCards();
    applyStoredVariantUrlsToUnifiedCardsOnly();
    syncCheckboxes();
    if (!hasDrawer) return;

    // Count badge
    const countBubble = drawer.querySelector('[data-compare-count]');
    if (countBubble) {
      countBubble.textContent = String(count);
      countBubble.hidden = (count === 0);
    }

    // Toggle aria-label
    if (toggle) {
      const badge = toggle.querySelector('[data-compare-count]');
      if (badge) badge.textContent = count;
      toggle.setAttribute(
        'aria-label',
        drawer.classList.contains('compare-drawer--minimized')
          ? (count > 0 ? `Open compare drawer — ${count} item${count === 1 ? '' : 's'}` : 'Open compare drawer — empty')
          : 'Hide compare drawer'
      );
    }

    drawer.hidden = (count === 0);

    const countElText = drawer.querySelector('[data-compare-count-text]');
    if (countElText) countElText.textContent = `Comparing ${count} of ${MAX}`;

    // Compare URL (prefer variant handles)
    const goLink = drawer.querySelector('[data-compare-go]');
    if (goLink) {
      const base = getBaseCompareUrl();
      const url = new URL(base, location.origin);

      const handlesForUrl = data.items.map(item => {
        if (item.vurl) {
          try {
            const h = new URL(item.vurl, location.origin).pathname.split('/').pop();
            if (h) return h;
          } catch (_) {}
        }
        return item.handle;
      });

      url.searchParams.set('compare', handlesForUrl.join(','));
      url.searchParams.set('models', data.items.map(x => x.modelKey).join(','));
      url.searchParams.set('colors', data.items.map(x => x.color || '').join(','));

      goLink.setAttribute('href', url.toString());
      if (count < 2) goLink.setAttribute('aria-disabled', 'true');
      else goLink.removeAttribute('aria-disabled');
    }

    // Chips
    const itemsEl = drawer.querySelector('[data-compare-items]');
    if (itemsEl) {
      itemsEl.innerHTML = '';
      data.items.slice(0, MAX).forEach(it => {
        const card = document.createElement('div');
        card.className = 'compare-card';
        card.setAttribute('role', 'listitem');

        // Pull best-available details from DOM (does NOT change storage)
        const dom = __lookupFromDomByKey(it.modelKey) || {};

        const chipTitle  = it.title || dom.title || displayModelName(it.modelKey) || it.handle || 'Selected item';
        const chipImg    = it.image || dom.image || '';
        const chipHandle = it.vurl ? '' : (it.handle || dom.handle || '');
        const href       = it.vurl || (chipHandle ? `/products/${chipHandle}` : '#');

        const imgHtml = chipImg ? `<img alt="${chipTitle}" loading="lazy" src="${chipImg}">` : '';

        card.innerHTML = `
          ${imgHtml}
          <div class="compare-card__title">
            <a href="${href}" class="compare-card__link">${chipTitle}</a>
          </div>
          <button class="compare-card__remove" aria-label="Remove ${chipTitle}" data-remove="${it.modelKey}">×</button>
        `;
        itemsEl.appendChild(card);
      });


      for (let i = data.items.length; i < MAX; i++) {
        const ph = document.createElement('div');
        ph.className = 'compare-card compare-card--placeholder';
        ph.setAttribute('role', 'listitem');
        itemsEl.appendChild(ph);
      }
    }

    setPanelHeight();
    drawer.classList.toggle('compare-drawer--open', count > 0);
  }

  /* ----------------- Listeners ----------------- */
  // Checkbox clicks on product cards / collection pages
  document.addEventListener('change', function (e) {
    const el = e.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'checkbox') return;
    const wrap = el.closest('[data-compare]');
    if (!wrap) return;

    const fallbackImg =
      wrap.getAttribute('data-product-image') ||
      (wrap.querySelector('img') && wrap.querySelector('img').getAttribute('src')) ||
      '';

    const fallbackTitle =
      wrap.getAttribute('data-product-title') ||
      (wrap.querySelector('img') && wrap.querySelector('img').getAttribute('alt')) ||
      (wrap.querySelector('[data-title]') && wrap.querySelector('[data-title]').textContent) ||
      '';

    const card = wrap.closest('.product-card, .grid-product, .results-col, [data-product-handle]');

    const selectedSwatch = card?.querySelector(
      '.color-variant-wrapper .bubble.active, .color-variant-wrapper .bubble.selected, .swatch__button.is-selected, .swatch__button[aria-pressed="true"]'
    );

    let vurlFromSwatch  = selectedSwatch?.dataset?.variantUrl || selectedSwatch?.getAttribute?.('href') || '';

    // Fallback: if no active swatch, try primary CTA link on the card
    if (!vurlFromSwatch && card) {
      const cta = card.querySelector('.bottom-col a.button, .bottom-col a.button--primary, a[href*="/products/"]');
      if (cta) vurlFromSwatch = cta.getAttribute('href') || '';
    }

    const colorFromSwatch = selectedSwatch?.dataset?.color
      || selectedSwatch?.getAttribute?.('data-value')
      || selectedSwatch?.getAttribute?.('aria-label')
      || '';

    // Helper: extract product handle from any URL
    const __handleFromUrl = (u) => {
      if (!u) return '';
      try {
        const url = new URL(u, location.origin);
        const parts = url.pathname.split('/').filter(Boolean);
        const idx = parts.indexOf('products');
        return (idx > -1 && parts[idx + 1]) ? parts[idx + 1] : (parts[parts.length - 1] || '');
      } catch {
        const path = (u || '').split('?')[0].split('#')[0];
        const parts = path.split('/').filter(Boolean);
        return parts[parts.length - 1] || '';
      }
    };

    // NEW: read perf key from the control or a containing node
    const perfFromAttr =
      wrap.getAttribute('data-perf-key') ||
      wrap.closest('[data-perf-key]')?.getAttribute('data-perf-key') ||
      '';

    const item = {
      handle:   wrap.getAttribute('data-product-handle')
              || card?.getAttribute?.('data-product-handle')
              || __handleFromUrl(vurlFromSwatch)
              || '',
      title:    fallbackTitle,
      image:    fallbackImg,
      modelKey: wrap.getAttribute('data-model-key')
              || wrap.getAttribute('data-product-type')
              || card?.getAttribute?.('data-product-type')
              || card?.getAttribute?.('data-model-key')
              || __handleFromUrl(vurlFromSwatch)  // let deriveModelKey pick it up
              || '',
      type:     wrap.getAttribute('data-product-type') || '',
      color:    colorFromSwatch || wrap.getAttribute('data-product-color') || '',
      vurl:     vurlFromSwatch  || wrap.getAttribute('data-variant-url') || '',
      // NEW: persist the exact variant id and perf key
      variantId: __extractVariantId(vurlFromSwatch || wrap.getAttribute('data-variant-url') || ''),
      perf: (wrap.getAttribute('data-perf-key') ||
            wrap.closest('[data-perf-key]')?.getAttribute('data-perf-key') || '').toLowerCase().trim()
    };

    if (!deriveModelKey(item)) { el.checked = false; return; }

    if (el.checked) {
      add(item);
      //ensureOpen();
      forceExpandDrawer();
    } else {
      const mk = deriveModelKey(item);
      if (mk) removeModelStrict(mk);
    }
    render();
  });

  // Remove chip in drawer
  if (hasDrawer && itemsEl) {
    itemsEl.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-remove]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const key = (btn.getAttribute('data-remove') || '').toLowerCase().trim();
      removeModelStrict(key);
      render();
    });
  }

  // Clear all (only react to user-initiated clicks)
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-compare-clear]');
    if (!btn) return;
    if (!e.isTrusted) return;
    e.preventDefault();
    clearAll();
    render();
  }, true);

  // Minimize toggle
  if (hasDrawer && toggle) {
    toggle.addEventListener('click', function () {
      const minimized = drawer.classList.toggle('compare-drawer--minimized');
      if (!minimized) setPanelHeight();
      toggle.setAttribute('aria-expanded', minimized ? 'false' : 'true');

      // MOD: Save the new state to sessionStorage.
      try {
        sessionStorage.setItem(DRAWER_STATE_KEY, minimized ? 'minimized' : 'expanded');
      } catch (_) {}
    });
  }

  /* ----------------- Cross-page / lifecycle wake-ups ----------------- */
  function markAndRenderIfSeqChanged() {
    const currentSeq = parseInt(localStorage.getItem(STORAGE_SEQ_KEY) || '0', 10) || 0;
    const lastSeen = parseInt(sessionStorage.getItem(PAGE_LAST_SEEN_SEQ) || '0', 10) || 0;
    if (currentSeq !== lastSeen) {
      sessionStorage.setItem(PAGE_LAST_SEEN_SEQ, String(currentSeq));
      render();
      return true;
    }
    return false;
  }

  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY || e.key === STORAGE_SEQ_KEY) {
      markAndRenderIfSeqChanged() || render();
    }
  });

  try {
    bc && bc.addEventListener('message', (msg) => {
      if (msg && msg.data && msg.data.type === 'compare:seq') {
        markAndRenderIfSeqChanged() || render();
      }
    });
  } catch (_) {}

  window.addEventListener('compare:changed', () => { markAndRenderIfSeqChanged() || render(); });

  // BFCache restore / back navigation: force a fresh read after paint
  function wakeAndRender() {
    //applyStoredVariantUrlsToCards();
    applyStoredVariantUrlsToUnifiedCardsOnly();
    markAndRenderIfSeqChanged() || render();
    Promise.resolve().then(() => { markAndRenderIfSeqChanged() || render(); });
    requestAnimationFrame(() => { markAndRenderIfSeqChanged() || render(); });
    setTimeout(() => { markAndRenderIfSeqChanged() || render(); }, 0);
    setPanelHeight();
  }
  window.addEventListener('pageshow', wakeAndRender);
  window.addEventListener('popstate', wakeAndRender);
  window.addEventListener('focus', () => { markAndRenderIfSeqChanged() || render(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') { markAndRenderIfSeqChanged() || render(); }
  });
  // After BFCache restore / back to a collection page, hydrate any missing titles/images
  //window.addEventListener('pageshow', () => setTimeout(__hydrateFromDomIfMissing, 50));
  /*document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') __hydrateFromDomIfMissing();
  });*/

  /* ---------- FINAL SAFETY NET: lightweight seq poller while page is visible ---------- */
  let __pollId = null;
  function __pollSeqTick() { markAndRenderIfSeqChanged(); }
  function __startPoll() {
    if (!__pollId) __pollId = setInterval(__pollSeqTick, 800);
  }
  function __stopPoll() {
    if (__pollId) { clearInterval(__pollId); __pollId = null; }
  }
  __startPoll();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') __startPoll();
    else __stopPoll();
  });

  // Initial layout calc
  document.addEventListener('DOMContentLoaded', setPanelHeight);

  /* ----------------- Swatch → label + storage sync ----------------- */
  document.addEventListener('click', (e) => {
    const swatch = e.target.closest('.color-variant-wrapper .bubble, .swatch__button, [data-variant-url]');
    if (!swatch) return;

    const card  = swatch.closest('.product-card, .grid-product, .results-col, [data-product-handle]');
    const label = card?.querySelector('.compare-checkbox[data-compare]');
    if (!label) return;

    const vurl  = swatch.dataset.variantUrl || swatch.getAttribute('href') || '';
    const color = swatch.dataset.color      || swatch.getAttribute('data-value') || swatch.getAttribute('aria-label') || '';

    if (vurl)  label.setAttribute('data-variant-url', vurl);
    if (color) label.setAttribute('data-product-color', color);

    try {
      const mk = canonicalModelKey(
        label.getAttribute('data-model-key') || label.getAttribute('data-product-type') || label.getAttribute('data-product-handle') || ''
      );
      if (!mk) return;

      const data = load();
      const idx  = data.items.findIndex(x => canonicalModelKey(x.modelKey) === mk);
      if (idx > -1) {
        const prev = data.items[idx];
        data.items[idx] = { ...prev, vurl: vurl || prev.vurl, color: color || prev.color };
        save(data.items);
      }
    } catch (_) {}
  });

  /* ----------------- Lazy-load awareness (MutationObserver) ----------------- */
  const observeLazy = new MutationObserver((mutations) => {
    let touched = false;
    for (const m of mutations) {
      if (m.addedNodes && m.addedNodes.length) {
        for (const n of m.addedNodes) {
          if (!(n instanceof HTMLElement)) continue;
          if (n.querySelector?.('[data-compare]') || n.matches?.('[data-compare]')) {
            touched = true;
            break;
          }
        }
      }
      if (touched) break;
    }
    if (touched) {
      requestAnimationFrame(() => {
        syncCheckboxes();
        setPanelHeight();
      });
    }
  });

  try {
    observeLazy.observe(document.body, { childList: true, subtree: true });
  } catch (_) {}

  /* ----------------- Compare page hooks ----------------- */

  // Pull best-available details for a given canonical model key from any [data-compare] card in the DOM
  function __lookupFromDomByKey(k) {
    k = canonicalModelKey(k);
    if (!k) return {};
    const els = document.querySelectorAll('[data-compare]');
    for (const el of els) {
      const obj = {
        modelKey: el.getAttribute('data-model-key') || el.getAttribute('data-product-type') || el.getAttribute('data-product-handle') || '',
        handle:   el.getAttribute('data-product-handle') || '',
        title:    el.getAttribute('data-product-title')  || '',
        image:    el.getAttribute('data-product-image')  || '',
        color:    el.getAttribute('data-product-color')  || '',
        vurl:     el.getAttribute('data-variant-url')    || ''
      };
      const mk = canonicalModelKey(obj.modelKey || obj.handle || obj.title);
      if (mk === k) {
        return {
          handle: obj.handle ? obj.handle.toLowerCase().trim() : '',
          title:  obj.title || '',
          image:  obj.image || '',
          color:  obj.color || '',
          vurl:   obj.vurl  || ''
        };
      }
    }
    return {};
  }

  // On non-compare pages, fill missing title/image/handle from DOM cards so chips never show slugs
  function __hydrateFromDomIfMissing() {
    if (isComparePage()) return; // only run on collection/product pages

    // 1. Scan the entire page just ONCE to build a quick reference map.
    const domDetailsMap = new Map();
    const allCompareCards = document.querySelectorAll('[data-compare]');
    if (!allCompareCards.length) return; // Early exit if no product cards are on the page.

    for (const el of allCompareCards) {
      const obj = {
        modelKey: el.getAttribute('data-model-key') || el.getAttribute('data-product-type') || el.getAttribute('data-product-handle') || '',
        handle:   el.getAttribute('data-product-handle') || '',
        title:    el.getAttribute('data-product-title')  || '',
        image:    el.getAttribute('data-product-image')  || '',
        color:    el.getAttribute('data-product-color')  || '',
        vurl:     el.getAttribute('data-variant-url')    || ''
      };
      const mk = canonicalModelKey(obj.modelKey || obj.handle || obj.title);
      
      // Only add the first instance found for each model to the map.
      if (mk && !domDetailsMap.has(mk)) {
        domDetailsMap.set(mk, obj);
      }
    }

    const data = load();
    let changed = false;

    const items = (data.items || []).map(it => {
      const mk = canonicalModelKey(it.modelKey);
      if (!mk) return it;

      // 2. Do a super-fast lookup from our pre-built map instead of scanning the page again.
      const dom = domDetailsMap.get(mk) || {};

      const nextTitle = dom.title || it.title || displayModelName(mk);
      const nextImage = dom.image || it.image || '';
      const nextHandle = dom.handle || it.handle || '';
      const nextVurl = dom.vurl || it.vurl || '';
      const nextColor = dom.color || it.color || '';

      if (
        nextTitle !== it.title ||
        nextImage !== it.image ||
        nextHandle !== it.handle ||
        nextVurl !== it.vurl ||
        nextColor !== it.color
      ) {
        changed = true;
        return { ...it, title: nextTitle, image: nextImage, handle: nextHandle, vurl: nextVurl, color: nextColor };
      }
      
      return it;
    });

    if (changed) {
      save(items);
      render(); // Force a re-render to show the updated, correct names immediately.
    }
  }

  // Ensure each [data-compare] reflects the stored variant handle (vurl)
  // so syncCheckboxes() can correctly mark the active variant after BFCache.
  function applyStoredVariantUrlsToCards() {
    const data = load();
    const byModel = new Map();

    (data.items || []).forEach(x => {
      const mk = deriveModelKey(x);
      if (!mk) return;
      let variantHandle = '';
      if (x.vurl) {
        try {
          const u = new URL(x.vurl, location.origin);
          variantHandle = (u.pathname.split('/').filter(Boolean).pop() || '').toLowerCase();
        } catch {
          variantHandle = (x.vurl || '').split('?')[0].split('#')[0].split('/').filter(Boolean).pop() || '';
          variantHandle = variantHandle.toLowerCase();
        }
      }
      byModel.set(mk, { variantHandle });
    });

    document.querySelectorAll('[data-compare]').forEach(el => {
      const mk = deriveModelKey({
        modelKey: el.getAttribute('data-model-key') ||
                  el.getAttribute('data-product-type') ||
                  el.getAttribute('data-product-handle') || ''
      });
      if (!mk) return;

      const chosen = byModel.get(mk);
      if (!chosen || !chosen.variantHandle) return;

      const have = (el.getAttribute('data-variant-url') || '')
        .split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
      const haveHandle = (have || '').toLowerCase();

      if (haveHandle !== chosen.variantHandle) {
        el.setAttribute('data-variant-url', `/products/${chosen.variantHandle}`);
      }
    });
  }

  // Only touch compare controls inside unified vc2 cards so normal tiles keep their own state
  function applyStoredVariantUrlsToUnifiedCardsOnly() {
    const data = load();
    const byModel = new Map();

    (data.items || []).forEach(x => {
      const mk = deriveModelKey(x);
      if (!mk) return;

      let variantHandle = '';
      if (x.vurl) {
        try {
          const u = new URL(x.vurl, location.origin);
          variantHandle = (u.pathname.split('/').filter(Boolean).pop() || '').toLowerCase();
        } catch {
          const tail = (x.vurl || '').split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
          variantHandle = (tail || '').toLowerCase();
        }
      }

      byModel.set(mk, {
        variantHandle,              // used to keep data-variant-url aligned
        perf: (x.perf || '').toString() // kept ONLY for comparison logic; DO NOT write to DOM
      });
    });

    // 🔒 Only update compare controls inside unified vc2 cards
    document.querySelectorAll('[id^="vc2-card-"] [data-compare]').forEach(el => {
      const mk = deriveModelKey({
        modelKey: el.getAttribute('data-model-key') ||
                  el.getAttribute('data-product-type') ||
                  el.getAttribute('data-product-handle') || ''
      });
      if (!mk) return;

      const chosen = byModel.get(mk);
      if (!chosen) return;

      // Keep variant-url aligned (if we have one). Do NOT touch perf on the element.
      if (chosen.variantHandle) {
        const have = (el.getAttribute('data-variant-url') || '')
          .split('?')[0].split('#')[0].split('/').filter(Boolean).pop();
        const haveHandle = (have || '').toLowerCase();

        if (haveHandle !== chosen.variantHandle) {
          el.setAttribute('data-variant-url', `/products/${chosen.variantHandle}`);
        }
      }
    });
  }


  // Detect compare page lazily (DOM may not be ready, BFCache, etc.)
  const isComparePage = () => !!document.querySelector('.results-col');

  // ✅ Turn URL → storage mirror back on (safe + order-preserving).
  //    This keeps the drawer in sync with the current URL when landing,
  //    when removing columns, or when navigating back/forward.
  /*
  function mirrorUrlToStorage() {
    if (!isComparePage()) return;

    const u = new URL(location.href);
    const modelsRaw = (u.searchParams.getAll('models') || []).filter(Boolean).join(',') || (u.searchParams.get('models') || '');
    const compareRaw = (u.searchParams.getAll('compare') || []).filter(Boolean).join(',') || (u.searchParams.get('compare') || '');
    const colorsRaw = (u.searchParams.getAll('colors') || []).filter(Boolean).join(',') || (u.searchParams.get('colors') || '');

    const modelKeysInUrl = modelsRaw.split(',').map(s => s.trim()).filter(Boolean);
    const compareHandles = compareRaw.split(',').map(s => s.trim()).filter(Boolean);
    const colorTokens    = colorsRaw.split(',').map(s => s.trim());
    const orderedKeys = (modelKeysInUrl.length ? modelKeysInUrl : compareHandles.map(canonicalModelKey)).map(canonicalModelKey).filter(Boolean);

    if (!orderedKeys.length) { return; }

    // --- New Cache Logic ---
    // Load our permanent directory of rich product details.
    let modelCache = {};
    try { modelCache = JSON.parse(localStorage.getItem(STORAGE_MODEL_CACHE_KEY) || '{}'); } catch (_) {}
    // --- End New Cache Logic ---

    const prev = load().items || [];
    const byKey = new Map(prev.map(p => [canonicalModelKey(p.modelKey), p]));

    const next = orderedKeys.map((k, i) => {
      const old = byKey.get(k) || {};
      const cached = modelCache[k] || {}; // Get details from our new cache
      const handleFromUrl = compareHandles[i] || '';
      const colorFromUrl  = colorTokens[i] || '';

      return {
        modelKey: k,
        // Prioritize cached data, then old data, then fallbacks.
        handle:   cached.handle || old.handle || handleFromUrl || '',
        title:    cached.title  || old.title  || displayModelName(k),
        image:    cached.image  || old.image  || '',
        color:    old.color   || colorFromUrl || '',
        vurl:     cached.vurl   || old.vurl   || ''
      };
    });
    save(next);
  }
  */
  // This function syncs the page state based on a clear priority:
  // 1. If URL parameters exist, they are the source of truth.
  // 2. If no URL parameters exist, but session data (localStorage) does, that data is used.
  // 3. If neither URL nor session data exists, the page's hardcoded defaults are used.
  function mirrorUrlToStorage() {
    if (!isComparePage()) return;

    let u = new URL(location.href);
    const hasUrlParams = u.searchParams.has('compare') || u.searchParams.has('models');

    // If the URL has no compare parameters, check for session data.
    // This allows a user's existing comparison session to hydrate the page.
    if (!hasUrlParams) {
      const sessionData = load();
      const hasSessionData = sessionData && sessionData.items && sessionData.items.length > 0;

      if (hasSessionData) {
        // Build URL parameters from the existing session data.
        const handles = sessionData.items.map(item => __extractHandleFromUrl(item.vurl) || item.handle).filter(Boolean);
        const models = sessionData.items.map(item => item.modelKey).filter(Boolean);
        const colors = sessionData.items.map(item => item.color || '');

        u.searchParams.set('compare', handles.join(','));
        u.searchParams.set('models', models.join(','));
        u.searchParams.set('colors', colors.join(','));
        
        // Update the browser's URL without reloading the page.
        history.replaceState({}, '', u.toString());
      } else {
        // No URL params and no session data. Let the page fall back to its
        // hardcoded defaults. We clear storage to ensure a clean state.
        //save([]);
        //render();
        // Leave defaults as-is; initializeComparePage() will handle the default→URL→sync flow.
        return;
      }
    }

    // --- The rest of the function proceeds, using the URL as the source of truth ---

    const modelsRaw = (u.searchParams.getAll('models') || []).filter(Boolean).join(',') || (u.searchParams.get('models') || '');
    const compareRaw = (u.searchParams.getAll('compare') || []).filter(Boolean).join(',') || (u.searchParams.get('compare') || '');
    const colorsRaw = (u.searchParams.getAll('colors') || []).filter(Boolean).join(',') || (u.searchParams.get('colors') || '');

    const modelKeysInUrl = modelsRaw.split(',').map(s => s.trim()).filter(Boolean);
    const compareHandles = compareRaw.split(',').map(s => s.trim()).filter(Boolean);
    const colorTokens    = colorsRaw.split(',').map(s => s.trim());
    const orderedKeys = (modelKeysInUrl.length ? modelKeysInUrl : compareHandles.map(canonicalModelKey)).map(canonicalModelKey).filter(Boolean);

    // If the URL parameters result in no valid models, clear storage and render an empty state.
    if (!orderedKeys.length) {
      save([]);
      render();
      return;
    }

    // Load caches to enrich the data.
    let modelCache = {};
    try { modelCache = JSON.parse(localStorage.getItem(STORAGE_MODEL_CACHE_KEY) || '{}'); } catch (_) {}

    const prev = load().items || [];
    const byKey = new Map(prev.map(p => [canonicalModelKey(p.modelKey), p]));

    const next = orderedKeys.map((k, i) => {
      const old = byKey.get(k) || {};
      const cached = modelCache[k] || {};
      const handleFromUrl = compareHandles[i] || ''; // This is the VARIANT handle
      const colorFromUrl  = colorTokens[i] || '';
      const vurlFromUrl = handleFromUrl ? `/products/${handleFromUrl}` : '';

      return {
        modelKey: k,
        handle:   cached.handle || old.handle || handleFromUrl || '',
        title:    cached.title  || old.title  || displayModelName(k),
        image:    cached.image  || old.image  || '',
        color:    old.color   || colorFromUrl || '',
        vurl:     cached.vurl   || old.vurl   || vurlFromUrl || ''
      };
    });
    
    save(next);
    applyStoredVariantUrlsToUnifiedCardsOnly();
    render();
  }

  // Column remove button (unchanged except comment)
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.remove-column-btn');
    if (!btn) return;
    if (!e.isTrusted) return;
    const col = btn.closest('.results-col');
    const resolved = (col?.dataset.item || btn.getAttribute('data-button') || '').toLowerCase().trim();
    if (!resolved) return;
    const mk = canonicalModelKey(resolved);
    if (mk) {
      removeModel(mk);
      render();
      // ✅ let the (updated) URL drive storage/order as well
      setTimeout(mirrorUrlToStorage, 60);
    }
  }, true);

  // Compare-page model checkboxes (keep your guard; unchanged behavior)
  document.addEventListener('change', function (e) {
    const el = e.target;
    if (!(el instanceof HTMLInputElement)) return;
    if (!e.isTrusted) return;

    if (el.name === 'select-model' && el.id !== 'all') {
      if (!el.checked) {
        const col = el.closest('.results-col');
        const resolved = (col?.dataset.item || el.value || '').toLowerCase().trim();
        const mk = canonicalModelKey(resolved);
        if (mk) {
          removeModel(mk);
          render();
          setTimeout(mirrorUrlToStorage, 60);
        }
      }
    }

    if (el.id === 'all' && el.name === 'select-model' && !el.checked) {
      clearAll();
      render();
      setTimeout(mirrorUrlToStorage, 60);
    }
  }, true);

  /* ----------------- Initialization Logic ----------------- */

  /**
   * Orchestrates the initial state of the compare page based on a clear priority:
   * 1. URL parameters (from a shared link).
   * 2. Session data (from the user's current session).
   * 3. Hardcoded page defaults (the 'checked' checkboxes in the HTML).
   */
  function initializeComparePage() {
    if (!isComparePage()) return;

    const u = new URL(location.href);
    const hasUrlParams = u.searchParams.has('compare') || u.searchParams.has('models');
    const sessionData = load();
    const hasSessionData = sessionData && sessionData.items && sessionData.items.length > 0;

    if (hasUrlParams) {
      // Priority 1: URL is the source of truth.
      // Sync localStorage to match the URL.
      mirrorUrlToStorage();
      return;
    }

    if (hasSessionData) {
      // Priority 2: Session data is the source of truth.
      // We must update the URL from the session data so the UI renders correctly.
      const handles = sessionData.items.map(item => __extractHandleFromUrl(item.vurl) || item.handle).filter(Boolean);
      const models = sessionData.items.map(item => item.modelKey).filter(Boolean);
      const colors = sessionData.items.map(item => item.color || '');

      u.searchParams.set('compare', handles.join(','));
      u.searchParams.set('models', models.join(','));
      u.searchParams.set('colors', colors.join(','));
      
      // Update the browser's URL without reloading the page.
      history.replaceState({}, '', u.toString());
      
      // Now that the URL is correct, sync storage to it.
      mirrorUrlToStorage();
      return;
    }

    // Priority 3: Page defaults are the source of truth.
    // Read the default checked boxes from the HTML and create URL params.
    const defaultHandles = [];
    document.querySelectorAll('input[name="select-model"]:checked').forEach(input => {
      if (input.value && input.value !== 'all') {
        defaultHandles.push(input.value);
      }
    });

    if (defaultHandles.length > 0) {
      const modelKeys = defaultHandles.map(canonicalModelKey).filter(Boolean);
      u.searchParams.set('compare', defaultHandles.join(','));
      u.searchParams.set('models', modelKeys.join(','));
      history.replaceState({}, '', u.toString());

      // Sync storage from the new URL created from the defaults.
      mirrorUrlToStorage();
    }
  }

  // Keep storage aligned with URL on history navigation (back/forward buttons)
  window.addEventListener('pageshow', mirrorUrlToStorage);
  window.addEventListener('popstate', mirrorUrlToStorage);
  
  // Use our new initialization function on the first page load.
  //document.addEventListener('DOMContentLoaded', initializeComparePage);
  if (isComparePage()) {
    // Run immediately (script is defer’d so markup is parsed)
    initializeComparePage();
    // And run once again after DOMContentLoaded to catch any late nodes
    document.addEventListener('DOMContentLoaded', initializeComparePage, { once: true });
  }


  function __extractHandleFromUrl(u) {
    if (!u) return '';
    try {
      const url = new URL(u, location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      return (idx > -1 && parts[idx + 1]) ? parts[idx + 1] : (parts[parts.length - 1] || '');
    } catch {
      const path = (u || '').split('?')[0].split('#')[0];
      const parts = path.split('/').filter(Boolean);
      return parts[parts.length - 1] || '';
    }
  }
  function __extractVariantId(u) {
    if (!u) return '';
    try {
      const url = new URL(u, location.origin);
      return (url.searchParams.get('variant') || '').trim();
    } catch {
      const m = (u || '').match(/[?&]variant=(\d+)/);
      return m ? m[1] : '';
    }
  }
  const __isUnifiedCardEl = (el) => !!el.closest('[id^="vc2-card-"]');

  function migrateFillModelKeys() {
    const data = load();
    let changed = false;
    data.items = (data.items || []).map(it => {
      const has = canonicalModelKey(it.modelKey || '');
      if (has) return it;

      // Try vurl → handle
      let mk =
        deriveModelKey(it) ||
        canonicalModelKey(__extractHandleFromUrl(it.vurl || '')) ||
        canonicalModelKey(it.handle || '') ||
        canonicalModelKey(it.title || '');

      if (mk) {
        changed = true;
        return { ...it, modelKey: mk };
      }
      return it;
    });

    if (changed) {
      __compareWriteGate.ok = true;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: data.items, ts: now() }));
      } finally {
        __compareWriteGate.ok = false;
      }
      bumpSeq();
      try { window.dispatchEvent(new CustomEvent('compare:changed', { detail: data.items })); } catch (_) {}
    }
  }


  /* ----------------- Initial paint ----------------- */
  /*
  // Initial migration + paint
  try { sessionStorage.setItem(PAGE_LAST_SEEN_SEQ, localStorage.getItem(STORAGE_SEQ_KEY) || '0'); } catch (_) {}
  migrateFillModelKeys();
  // MOD: Apply the saved drawer state before the first render.
  applyInitialDrawerState();
  render();
  */

  try { sessionStorage.setItem(PAGE_LAST_SEEN_SEQ, localStorage.getItem(STORAGE_SEQ_KEY) || '0'); } catch (_) {}
  migrateFillModelKeys();
  applyInitialDrawerState();
  
  // 🚀 If we're on the compare page, initialize & sync NOW (no waiting for DOMContentLoaded)
  if (isComparePage()) {
    initializeComparePage(); // this calls mirrorUrlToStorage() which calls save() + render()
  } else {
    render();
  }

})();